import {
  useEffect, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { isMarkerStyle } from '~/_shared/types/marker.types';
import { useIsMapInteractionActive } from '~/_shared/utils/hooks/useIsMapInteractionActive';
import { useGetMarkerEntityStyleFromMarkerSettings } from '~/_shared/utils/markers/useGetMarkerEntityStyleFromMarkerSettings.hook';
import { usePlacesSearchZIndexes } from '~/map/zIndexes/usePlacesSearchZIndexes.hook';
import {
  getPlacesMarkerProps, getPlacesToolCircleColors,
} from '~/sidebar/sidebarApps/mapTools/placesTool/placesTool.helpers';
import {
  activeElementsSetPlaceHover, activeElementsSetPlaceHoverEnd, activeMapElementsSetPlace,
} from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import type { PlacesSearchResult } from '~/store/frontendState/places/places.state';
import type { PlacesSearch } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import { useHiddenPlaceSearchIdsSelector } from '~/store/mapSettings/toolsState/visibility/visibility.selectors';
import { useMapContext } from '../mapContext';
import { registerMarkerTemplate } from '../markers/useMarkers/useMarkerStyles.hook';
import type { MapPlacesManager } from './mapPlacesManager';

export const PlacesSearchInstance = ({
  item,
  results,
  manager,
}: {
  item: PlacesSearch;
  results: PlacesSearchResult;
  manager: MapPlacesManager;
}) => {
  const dispatch = useDispatch();
  const { markerTemplateManager } = useMapContext();
  const hiddenPlaceSearchIds = useHiddenPlaceSearchIdsSelector();
  const isMapInteractionActive = useIsMapInteractionActive();
  const getMarkerEntityStyleFromMarkerSettings = useGetMarkerEntityStyleFromMarkerSettings();

  const isHidden = hiddenPlaceSearchIds.includes(item.id);

  const placesOrdered = useMemo(() => {
    return results.items.map((placeItem, originalIndex) => ({
      ...placeItem,
      originalIndex,
      searchId: item.id,
    })).sort((a, b) => b.location.lat - a.location.lat);
  }, [results.items, item.id]);

  const zIndexes = usePlacesSearchZIndexes(placesOrdered);

  const markerEntityStyle = useMemo(() => {
    const markerSettings = getPlacesMarkerProps({ placeColor: item.color });
    return getMarkerEntityStyleFromMarkerSettings(markerSettings.markerSettings);
  }, [getMarkerEntityStyleFromMarkerSettings, item.color]);

  const renderData = useMemo(() => {
    return placesOrdered.map((place, index) => {
      const circleColors = getPlacesToolCircleColors(item.color);
      return {
        id: place.id,
        marker: markerEntityStyle,
        backgroundColor: circleColors.backgroundColor.hex,
        fontColor: circleColors.color.hex,
        numberLabel: (place.originalIndex + 1).toString(),
        latLng: place.location,
        zIndex: zIndexes[index] ?? { marker: 0, circle: 0, text: 0 },
        onClick: isMapInteractionActive ? undefined : () => {
          dispatch(activeMapElementsSetPlace({
            id: place.id,
            placeSearchId: item.id,
          }));
        },
        onMouseEnter: isMapInteractionActive ? undefined : () => {
          dispatch(activeElementsSetPlaceHover({
            id: place.id,
            placeSearchId: item.id,
          }));
        },
        onMouseLeave: isMapInteractionActive ? undefined : () => {
          dispatch(activeElementsSetPlaceHoverEnd());
        },
      };
    });
  }, [placesOrdered, item.color, item.id, markerEntityStyle, zIndexes, isMapInteractionActive, dispatch]);

  useEffect(() => {
    if (isMarkerStyle(markerEntityStyle)) {
      registerMarkerTemplate(markerEntityStyle, markerTemplateManager);
    }

    if (isHidden) {
      return;
    }

    manager.addPlacesSearch({
      id: item.id,
      items: renderData,
    });

    return () => {
      manager.removePlacesSearch(item.id);
    };
  }, [item.id, manager, markerEntityStyle, markerTemplateManager, renderData, isHidden]);

  return null;
};
